/**
 * CircuitCard — one row in the project's CircuitsTab. Reads as a small
 * drafting plate: kind badge top-left, status pill, version chip, an
 * IntersectionObserver-gated kind-histogram thumbnail, an optional
 * 2-line description preview, and an expand toggle that reveals the
 * per-circuit version timeline + the full description.
 *
 * Lifecycle gestures (in the menu) are gated by status:
 *   * scratch / ephemeral → Promote
 *   * draft / active      → Branch · Release · Archive
 *   * released            → Branch (forks new draft) · Archive
 *   * archived            → Restore
 *
 * Release flow goes through `EditCircuitNotesDialog` so the user
 * captures optional markdown release notes alongside the status
 * transition (single PATCH carries both).
 */
import { CircuitKindBadge } from "@features/projects/components/CircuitKindBadge";
import { VersionBadge } from "@features/versions/components/VersionBadge";
import ArchiveIcon from "@mui/icons-material/ArchiveOutlined";
import BranchIcon from "@mui/icons-material/CallSplitRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import ReleasedIcon from "@mui/icons-material/LockRounded";
import MoreIcon from "@mui/icons-material/MoreVertRounded";
import NotesIcon from "@mui/icons-material/NotesRounded";
import OpenIcon from "@mui/icons-material/OpenInNewRounded";
import RestoreIcon from "@mui/icons-material/UnarchiveRounded";
import PromoteIcon from "@mui/icons-material/UpgradeRounded";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type { CircuitDTO, PatchCircuitRequest } from "@models/circuit-ir";
import { usePatchCircuit } from "../hooks/useCircuitLifecycle";
import { CircuitLineageChain } from "./CircuitLineageChain";
import { CircuitThumbnail } from "./CircuitThumbnail";
import { CircuitVersionTimeline } from "./CircuitVersionTimeline";
import { EditCircuitNotesDialog } from "./EditCircuitNotesDialog";

interface CircuitCardProps {
  circuit: CircuitDTO;
  isActive?: boolean;
  onBranch: () => void;
  onPromote: () => void;
  onArchive: () => void;
  onRestore: () => void;
  /** Fires when the user picks "Merge into main" on a candidate card.
   *  Disabled when the circuit isn't a candidate or there's no active
   *  main board to merge into. */
  onMerge?: () => void;
}

export function CircuitCard({
  circuit,
  isActive,
  onBranch,
  onPromote,
  onArchive,
  onRestore,
  onMerge,
}: CircuitCardProps) {
  const navigate = useNavigate();
  const patch = usePatchCircuit();
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [descDialog, setDescDialog] = useState(false);
  const [releaseDialog, setReleaseDialog] = useState(false);
  const closeMenu = () => setMenuEl(null);

  const versionLabel =
    circuit.current_version_semver?.trim() ||
    (circuit.current_version_number != null
      ? `v${circuit.current_version_number}`
      : null);

  const canPromote =
    circuit.status === "ephemeral" || circuit.status === "draft";
  const canBranch =
    circuit.status === "active" || circuit.status === "released";
  const canRelease = circuit.status === "active";
  const canArchive =
    circuit.status === "draft" ||
    circuit.status === "active" ||
    circuit.status === "released";
  const canRestore = circuit.status === "archived";
  const canMerge =
    !!onMerge &&
    circuit.kind === "candidate" &&
    (circuit.status === "draft" || circuit.status === "active");

  const hasDescription = !!circuit.description?.trim();
  const hasReleaseNotes = !!circuit.release_notes_md?.trim();

  const applyPatch = (payload: PatchCircuitRequest) =>
    patch.mutate({ circuitId: circuit.id, payload });

  return (
    <Box
      sx={(t) => ({
        position: "relative",
        bgcolor: isActive ? "accent.softer" : "surface.raised",
        border: "1px solid",
        borderColor: isActive ? "primary.main" : "border.default",
        borderRadius: 2,
        p: 1.75,
        transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}, transform ${t.motion.fast}, box-shadow ${t.motion.fast}`,
        "&:hover": {
          borderColor: alpha(t.palette.primary.main, 0.5),
          transform: expanded ? "none" : "translateY(-1px)",
          boxShadow: `0 6px 16px -10px ${alpha(t.palette.primary.main, 0.45)}`,
        },
        ...(circuit.parent_circuit_id && {
          "&::before": {
            content: '""',
            position: "absolute",
            top: 12,
            bottom: 12,
            left: -1,
            width: 2,
            borderRadius: 1,
            background: t.palette.editorial.main,
            opacity: 0.6,
          },
        }),
      })}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ minWidth: 0 }}
      >
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
          >
            <CircuitKindBadge kind={circuit.kind} />
            {versionLabel && (
              <VersionBadge
                versionNumber={circuit.current_version_number}
                semverLabel={circuit.current_version_semver}
                isCurrent
              />
            )}
          </Stack>
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.875rem",
            }}
          >
            {circuit.name}
          </Typography>
          {(circuit.component_count != null || circuit.net_count != null) && (
            <Typography
              variant="caption"
              sx={(t) => ({
                color: "text.disabled",
                fontFamily: t.fonts.mono,
                fontSize: 11,
              })}
            >
              {circuit.component_count ?? "—"} comps ·{" "}
              {circuit.net_count ?? "—"} nets
            </Typography>
          )}
          {circuit.current_version_id && (
            <CircuitThumbnail versionId={circuit.current_version_id} />
          )}
          {hasDescription && !expanded && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                lineHeight: 1.45,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {circuit.description}
            </Typography>
          )}
          {circuit.parent_circuit_id && !expanded && (
            <Typography
              variant="caption"
              sx={(t) => ({
                color: "text.disabled",
                fontFamily: t.fonts.mono,
                fontSize: 10,
              })}
            >
              ↳ branched · expand for lineage
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Open in chat">
            <IconButton
              size="small"
              onClick={() =>
                void navigate({
                  to: "/projects/$projectId/chat",
                  params: { projectId: circuit.project_id },
                })
              }
            >
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lifecycle actions">
            <IconButton
              size="small"
              onClick={(e) => setMenuEl(e.currentTarget)}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={expanded} mountOnEnter unmountOnExit>
        <Box sx={{ mt: 1.25 }}>
          <DescriptionSection
            circuit={circuit}
            onEdit={() => setDescDialog(true)}
          />

          {circuit.parent_circuit_id && (
            <CircuitLineageChain circuit={circuit} />
          )}

          <CircuitVersionTimeline circuit={circuit} />

          {hasReleaseNotes && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="overline"
                sx={(t) => ({
                  color: t.palette.editorial.main,
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  display: "block",
                  mb: 0.5,
                })}
              >
                Release notes
              </Typography>
              <Box
                sx={(t) => ({
                  pl: 1.25,
                  borderLeft: `2px solid ${alpha(t.palette.success.main, 0.6)}`,
                  py: 0.25,
                })}
              >
                <Typography
                  variant="body2"
                  sx={(t) => ({
                    color: "text.primary",
                    fontFamily: t.fonts.mono,
                    fontSize: 12,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  })}
                >
                  {circuit.release_notes_md}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Expand toggle — full-width hairline strip so the click target
          is generous even on small cards. */}
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={expanded ? "Collapse circuit" : "Expand circuit"}
        sx={(t) => ({
          mt: 1.25,
          mx: -1.75,
          mb: -1.75,
          px: 1.75,
          py: 0.5,
          width: "calc(100% + 28px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          cursor: "pointer",
          appearance: "none",
          background: "transparent",
          border: "none",
          borderTop: `1px dashed ${alpha(t.palette.border.default, 0.7)}`,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          color: "text.disabled",
          fontFamily: t.fonts.mono,
          fontSize: 10,
          letterSpacing: "0.14em",
          transition: `background-color ${t.motion.fast}, color ${t.motion.fast}`,
          "&:hover": {
            bgcolor: alpha(t.palette.primary.main, 0.04),
            color: "text.secondary",
          },
        })}
      >
        {expanded ? (
          <>
            <ExpandLessIcon sx={{ fontSize: 14 }} /> COLLAPSE
          </>
        ) : (
          <>
            <ExpandMoreIcon sx={{ fontSize: 14 }} /> HISTORY
          </>
        )}
      </Box>

      <Menu
        open={!!menuEl}
        anchorEl={menuEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setDescDialog(true);
          }}
        >
          <NotesIcon fontSize="small" sx={{ mr: 1 }} />{" "}
          {hasDescription ? "Edit description" : "Add description"}
        </MenuItem>
        {canPromote && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onPromote();
            }}
          >
            <PromoteIcon fontSize="small" sx={{ mr: 1 }} /> Promote
          </MenuItem>
        )}
        {canBranch && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onBranch();
            }}
          >
            <BranchIcon fontSize="small" sx={{ mr: 1 }} /> Branch as candidate
          </MenuItem>
        )}
        {canMerge && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onMerge?.();
            }}
            sx={{ color: "secondary.main" }}
          >
            <BranchIcon
              fontSize="small"
              sx={{ mr: 1, transform: "scaleX(-1)" }}
            />{" "}
            Merge into main…
          </MenuItem>
        )}
        {canRelease && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setReleaseDialog(true);
            }}
          >
            <ReleasedIcon fontSize="small" sx={{ mr: 1 }} /> Release with notes…
          </MenuItem>
        )}
        {canArchive && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onArchive();
            }}
            sx={{ color: "error.main" }}
          >
            <ArchiveIcon fontSize="small" sx={{ mr: 1 }} /> Archive
          </MenuItem>
        )}
        {canRestore && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onRestore();
            }}
          >
            <RestoreIcon fontSize="small" sx={{ mr: 1 }} /> Restore
          </MenuItem>
        )}
      </Menu>

      <EditCircuitNotesDialog
        open={descDialog}
        field="description"
        circuitName={circuit.name}
        initialValue={circuit.description}
        onSave={(value) => applyPatch({ description: value })}
        onClose={() => setDescDialog(false)}
      />

      <EditCircuitNotesDialog
        open={releaseDialog}
        field="release_notes_md"
        circuitName={circuit.name}
        initialValue={circuit.release_notes_md}
        saveAsRelease
        onSave={(value) =>
          applyPatch({
            status: "released",
            release_notes_md: value,
          })
        }
        onClose={() => setReleaseDialog(false)}
      />
    </Box>
  );
}

interface DescriptionSectionProps {
  circuit: CircuitDTO;
  onEdit: () => void;
}

function DescriptionSection({ circuit, onEdit }: DescriptionSectionProps) {
  const has = !!circuit.description?.trim();
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography
          variant="overline"
          sx={(t) => ({
            color: t.palette.editorial.main,
            fontSize: 10,
            letterSpacing: "0.16em",
          })}
        >
          Description
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={onEdit}
          sx={{ minWidth: 0, fontSize: 11, py: 0.25 }}
        >
          {has ? "Edit" : "Add"}
        </Button>
      </Stack>
      {has ? (
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {circuit.description}
        </Typography>
      ) : (
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", fontStyle: "italic" }}
        >
          No description yet — capture what this circuit is for.
        </Typography>
      )}
    </Box>
  );
}
